import { useMutation, useQuery } from '@apollo/client';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { Button } from 'components/Button/Button';
import { Card } from 'components/Card/Card';
import { Container } from 'components/Container/Container';
import { Icon } from 'components/Icon/Icon';
import { Input } from 'components/Input/Input';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { Select } from 'components/Select/Select';
import { Textarea } from 'components/Textarea/Textarea';
import { useToast } from 'components/Toast/use-toast';
import { CustomToggle } from 'components/Toggle/Toggle';
import { INFORMATION_TYPES } from 'lib/options';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AppRoutes } from 'Routes';
import { INFORMATION_CREATE, INFORMATION_UPDATE } from 'services/information/mutation';
import { INFORMATION } from 'services/information/query';

export const EditInformation = () => {
    const { t } = useTranslation('information');
    const { t: b } = useTranslation('translation');
    const { id } = useParams();
    const isEditing = !!id;
    const { data, refetch } = useQuery(INFORMATION);
    const [information, setInformation] = useState<any>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [existingDocument, setExistingDocument] = useState<any>(null);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [existingImage, setExistingImage] = useState<any>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { register, watch, handleSubmit, control, setValue } = useForm();
    const { toast } = useToast();
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);

    const [updateInformationMutation] = useMutation(INFORMATION_UPDATE);
    const [createInformationMutation] = useMutation(INFORMATION_CREATE);

    useEffect(() => {
        if (isEditing && data?.informations?.edges) {
            const foundInformation = data.informations.edges.find((edge: any) => edge.node.id === id);
            if (foundInformation) {
                const info = foundInformation.node;
                setValue('title', info.title);
                setValue('description', info.description);
                setValue('status', info.status === 'ACTIVE' ? true : false);
                setValue('type', INFORMATION_TYPES.find(type => type.value === info.informationType) || null);
                setValue('sortingNumber', info.sortingNumber || 0);
                setInformation(info);
                // Handle existing document
                if (info.document?.url) {
                    setExistingDocument(info.document);
                }
                // Handle existing image
                if (info.image?.url) {
                    setExistingImage(info.image);
                }
            }
        }
    }, [data, setValue, id, isEditing]);

    const onSubmit = (formData: any) => {
        if (isEditing && information?.id) {
            updateInformation(formData);
        } else {
            createInformation(formData);
        }
    };

    const createInformation = async (formData: any) => {
        try {
            setIsLoading(true);
            formData.status = formData.status ? 'ACTIVE' : 'INACTIVE';
            formData.informationType = formData.type?.value;
            formData.sortingNumber = formData.sortingNumber || 0;
            delete formData.type;
            
            const resp = await createInformationMutation({
                variables: { input: formData },
            });
            if (resp.data?.informationCreate?.information?.id) {
                successToast();
                refetch();
                navigate(AppRoutes.information);
            } else {
                failToast();
            }
        } catch (error) {
            failToast();
        } finally {
            setIsLoading(false);
        }
    };
    const updateInformation = async (formData: any) => {
        try {
            setIsLoading(true);
            formData.status = formData.status ? 'ACTIVE' : 'INACTIVE';
            formData.informationType = formData.type?.value;
            formData.sortingNumber = formData.sortingNumber || 0;
            delete formData.type;
            
            const resp = await updateInformationMutation({
                variables: { information: information.id, input: formData },
            });
            if (resp.data?.informationUpdate?.information?.id) {
                successToast();
                refetch();
                navigate(AppRoutes.information);
            } else {
                failToast();
            }
        } catch (error) {
            failToast();
        } finally {
            setIsLoading(false);
        }
    };

    const successToast = () => {
        toast({
            title: t('successToastTitle'),
            variant: 'success',
        });
    };

    const failToast = () => {
        toast({
            title: t('failToastTitle'),
            description: t('failToastSubtitle'),
            variant: 'warning',
        });
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setValue('document', file);
        }
    };

    const removeFile = () => {
        setSelectedFile(null);
        setValue('document', null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeExistingDocument = () => {
        setExistingDocument(null);
        // Set document to null to indicate removal in the update
        setValue('document', null);
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedImage(file);
            setValue('image', file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        setValue('image', null);
        if (imageInputRef.current) {
            imageInputRef.current.value = '';
        }
    };

    const removeExistingImage = () => {
        setExistingImage(null);
        setValue('image', null);
    };

    const downloadDocument = (url: string, name: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = name || 'document.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getFileName = (file: File) => {
        return file.name;
    };

    const getDocumentName = (document: any) => {
        if (document.name) {
            return document.name;
        }
        // Extract filename from URL if no name is provided
        if (document.url) {
            const urlParts = document.url.split('/');
            const filename = urlParts[urlParts.length - 1];
            // Remove query parameters if any
            return filename.split('?')[0] || 'document.pdf';
        }
        return 'document.pdf';
    };

    return (
        <Container>
            <Breadcrumbs crumbs={[b('breadcrumbs.dashboard'), b('breadcrumbs.information'), isEditing ? b('breadcrumbs.edit') : b('breadcrumbs.new')]} />
            <PageTitle withBack>{isEditing ? t('editTitle') : t('createTitle')}</PageTitle>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mt-4">
                    <Card className="flex flex-col gap-6">
                        <div>
                            <div className="text-sm font-medium mb-2">{t('titleLabel')}</div>
                            <Input placeholder="Title" {...register('title')} />
                        </div>
                        <div>
                            <div className="text-sm font-medium mb-2">{t('typeLabel') || 'Type'}</div>
                            <Controller
                                control={control}
                                name="type"
                                render={({ field: { onChange, value } }) => (
                                    <Select
                                        options={INFORMATION_TYPES}
                                        value={value}
                                        onChange={onChange}
                                        placeholder={t('selectInformationType')}
                                        isClearable
                                    />
                                )}
                            />
                        </div>
                        <div>
                            <div className="text-sm font-medium mb-2">{t('sortingNumberLabel')}</div>
                            <Input 
                                type="number" 
                                placeholder="0" 
                                {...register('sortingNumber', { 
                                    valueAsNumber: true,
                                    min: 0,
                                    validate: (value) => value >= 0 || 'Must be a positive number'
                                })} 
                            />
                        </div>
                        <div>
                            <div className="text-sm font-medium mb-4">{t('descriptionLabel')}</div>
                            <Textarea placeholder="Description" {...register('description')} />
                            <div className="flex justify-end text-sm mt-2 text-zinc-400">
                                {watch('description')?.length || 0} / 350
                            </div>
                        </div>
                        <div>
                            <div className="text-sm font-medium mb-4">{t('documentLabel')}</div>
                            
                            {/* Show existing document */}
                            {existingDocument && !selectedFile && (
                                <div className="flex gap-4 h-[40px] mb-4">
                                    <div className="py-[10px] px-3 bg-blue-50 border border-blue-200 font-medium flex justify-between gap-2 rounded flex-1">
                                        <div className="flex gap-2 items-center">
                                            <Icon name="file" className="h-4 w-4" />
                                            <div className="text-sm max-w-[200px] overflow-hidden truncate">
                                                {getDocumentName(existingDocument)}
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button 
                                                type="button" 
                                                onClick={() => downloadDocument(existingDocument.url, getDocumentName(existingDocument))}
                                                className="text-blue-600 hover:text-blue-800"
                                                title="Download"
                                            >
                                                <Icon name="download" className="h-4 w-4" />
                                            </button>
                                            <button type="button" onClick={removeExistingDocument}>
                                                <Icon name="close" className="text-zinc-500 hover:cursor-pointer h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Show new selected file */}
                            {selectedFile && (
                                <div className="flex gap-4 h-[40px] mb-4">
                                    <div className="py-[10px] px-3 bg-rose-50 border border-rose-200 font-medium flex justify-between gap-2 rounded flex-1">
                                        <div className="flex gap-2 items-center">
                                            <Icon name="file" className="h-4 w-4" />
                                            <div className="text-sm max-w-[200px] overflow-hidden truncate">{getFileName(selectedFile)}</div>
                                        </div>
                                        <button type="button" onClick={removeFile}>
                                            <Icon name="close" className="text-zinc-500 hover:cursor-pointer h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Upload button - always show unless there's a selected file */}
                            {!selectedFile && (
                                <Button 
                                    variant="outline" 
                                    onClick={() => fileInputRef.current?.click()} 
                                    type="button"
                                    className="w-fit"
                                >
                                    <Icon name="upload" className="h-4 w-4 mr-2" />
                                    {existingDocument ? t('replaceDocument') : t('uploadDocument')}
                                </Button>
                            )}

                            <input
                                multiple={false}
                                type="file"
                                hidden
                                onChange={handleFileChange}
                                ref={fileInputRef}
                                accept="application/pdf"
                            />                        
                        </div>
                        <div>
                            <div className="text-sm font-medium mb-4">{t('imageLabel')}</div>
                            
                            {/* Show existing image or new image preview */}
                            {(existingImage && !selectedImage) && (
                                <div className="mb-4">
                                    <div className="relative w-[180px] h-[180px] bg-zinc-100 rounded">
                                        <img 
                                            src={existingImage.url} 
                                            alt="Current thumbnail" 
                                            className="w-full h-full object-cover rounded"
                                        />
                                        <button 
                                            type="button" 
                                            onClick={removeExistingImage}
                                            className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-zinc-100"
                                        >
                                            <Icon name="close" className="text-zinc-500 h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Show new selected image preview */}
                            {imagePreview && selectedImage && (
                                <div className="mb-4">
                                    <div className="relative w-[180px] h-[180px] bg-zinc-100 rounded">
                                        <img 
                                            src={imagePreview} 
                                            alt="Preview" 
                                            className="w-full h-full object-cover rounded"
                                        />
                                        <button 
                                            type="button" 
                                            onClick={removeImage}
                                            className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-zinc-100"
                                        >
                                            <Icon name="close" className="text-zinc-500 h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Upload button - always show unless there's a selected image */}
                            {!selectedImage && (
                                <Button 
                                    variant="outline" 
                                    onClick={() => imageInputRef.current?.click()} 
                                    type="button"
                                    className="w-fit"
                                >
                                    <Icon name="upload" className="h-4 w-4 mr-2" />
                                    {existingImage ? t('replaceImage') : t('uploadImage')}
                                </Button>
                            )}

                            <input
                                multiple={false}
                                type="file"
                                hidden
                                onChange={handleImageChange}
                                ref={imageInputRef}
                                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                            />
                        </div>
                        <label className="flex items-center gap-2">
                            <Controller
                                control={control}
                                name="status"
                                render={({ field: { onChange, value } }) => (
                                    <CustomToggle icons={false} onChange={onChange} checked={value} />
                                )}
                            />

                            <span className="text-sm font-medium">{t('activeAnnouncementLabel')}</span>
                        </label>
                    </Card>
                </div>
                <div className="mt-8 flex justify-end">
                    <Button disabled={isLoading}>
                        {isLoading ? t('saving') : t('saveButton')}
                    </Button>
                </div>
            </form>
        </Container>
    );
};
